import { useEffect } from "react";

import { graphql, useFragment } from "react-relay";
import { Navigate, Outlet, useMatch } from "react-router";

import { paths } from "app/constants/paths";
import { useAlertContext } from "app/contexts/AlertContext";
import { skipOnboardingForLocalDev } from "app/utils/localDev";

import type { AppRoutesLayout_accounts$key } from "__generated__/AppRoutesLayout_accounts.graphql";

/**
 * Data passed to descendant routes via outlet context. `jun0Url` comes from
 * the AppRoutesLayout_accounts fragment — single GraphQL query, no parallel
 * useJun0Url hook.
 */
export interface RoutesOutletContext {
  jun0Url: string | null;
}

interface Props {
  /** Fragment reference from AppQuery. */
  queryRef: AppRoutesLayout_accounts$key;
}

/**
 * Renders the routed content region of the app shell: surfaces account
 * problems as alerts, gates the app behind onboarding until an account
 * exists, and hands fragment data to child routes via outlet context.
 */
export function AppRoutesLayout({ queryRef }: Props) {
  const data = useFragment(
    graphql`
      fragment AppRoutesLayout_accounts on Query {
        accounts {
          problems {
            message
          }
          pageInfo {
            total
          }
        }
        UrlConfig {
          jun0
        }
        whoami {
          __typename
          config {
            isOnboardingSkipped
          }
        }
      }
    `,
    queryRef,
  );

  const { alertDispatch } = useAlertContext();

  const problems = data.accounts.problems;

  useEffect(() => {
    problems.forEach((problem) => {
      alertDispatch({
        message: problem.message,
        severity: "error",
      });
    });
  }, [problems, alertDispatch]);

  const hasProblems = data.accounts.problems.length > 0;
  const hasAccounts = (data.accounts.pageInfo.total ?? 0) > 0;
  // Plumbed in via AppRoutesLayout_accounts so we don't need a parallel
  // useJun0Url query.
  const jun0Url = data.UrlConfig?.jun0 ?? null;

  const onOnboardingRoute = useMatch(paths.onboarding) !== null;

  if (
    !hasAccounts &&
    !hasProblems &&
    !data.whoami.config?.isOnboardingSkipped &&
    // Local-dev convenience: the stack rarely has an AWS account seeded, so the
    // onboarding gate would otherwise trap every route (see app/utils/localDev).
    !skipOnboardingForLocalDev() &&
    !onOnboardingRoute
  ) {
    return <Navigate to={paths.onboarding} />;
  }

  return <Outlet context={{ jun0Url } satisfies RoutesOutletContext} />;
}
